import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import PrimaryButton from "../../components/ui/PrimaryButton";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuid } from "uuid";
import BotMsgNode from "./BotMsgNode";
import StartNode from "./StartNode";
import UserMsgNode from "./UserMsgNode";
import StopNode from "./StopNode";
import MakeCallPopup from "./MakeCallPopup";
import { debounce } from "lodash";
import FinalDataTextArea from "./FinalDataTextArea";
import { useParams } from "react-router-dom";
import AuthContext from "../../store/AuthContext";
import { getFlowById, setFlowById } from "../../services/flowService";
import { getfinalDataObj } from "./common/common";

const nodeTypes = {
  botMsgNode: BotMsgNode,
  startNode: StartNode,
  userMsgNode: UserMsgNode,
  stopNode: StopNode,
};

const FlowChart = () => {
  const params = useParams();

  //debounced functions - have to make separate, even for same api so that one doesnt cancel another

  const debouncedSetFlowAPINodes = useRef(
    debounce((dataObj) => setFlowById(params.id, dataObj), 500)
  ).current;
  const debouncedSetFlowAPIEdges = useRef(
    debounce((dataObj) => setFlowById(params.id, dataObj), 500)
  ).current;
  const debouncedSetFlowAPIInterruptions = useRef(
    debounce((dataObj) => setFlowById(params.id, dataObj), 500)
  ).current;
  //   const debouncedSetBackendJson = useRef(
  //     debounce((dataObj) => {
  //       setFlowById(params.id, dataObj);
  //     }, 500)
  //   ).current;

  const { openSidebar, closeSidebar } = useContext(AuthContext);

  useEffect(() => {
    closeSidebar();
    return () => {
      openSidebar();
    };
  }, [openSidebar, closeSidebar]);

  // React Flow

  const [nodes, setNodes, onNodesChange] = useNodesState([
    {
      id: "start-node",
      next: "7dacde48-4fe7-45da-8d77-7e75d23f6cc7",
      type: "startNode",
      position: {
        x: 50,
        y: 250,
      },
      data: {
        name: "Start",
      },
      width: 54,
      height: 40,
    },
  ]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const [dataFetched, setDataFetched] = useState(false);

  useEffect(() => {
    const getFlowFromBackend = async () => {
      const flow = await getFlowById(params.id);
      if (flow) {
        if (flow.initial_nodes) setNodes(flow.initial_nodes);
        if (flow.initial_edges) setEdges(flow.initial_edges);
        if (flow.interruptions) setInterruptions(flow.interruptions);
        setDataFetched(true);
      }
    };

    getFlowFromBackend();
  }, [params.id, setEdges, setNodes]);

  useEffect(() => {
    if (!dataFetched) return;
    debouncedSetFlowAPINodes({ initial_nodes: nodes });
    if (nodes.length !== 0)
      nodes.forEach((node) => (node.data.setNodes = setNodes));

    return () => {
      debouncedSetFlowAPINodes.cancel();
    };
  }, [nodes, dataFetched, setNodes, debouncedSetFlowAPINodes]);

  useEffect(() => {
    if (!dataFetched) return;
    debouncedSetFlowAPIEdges({ initial_edges: edges });

    return () => {
      debouncedSetFlowAPIEdges.cancel();
    };
  }, [edges, dataFetched, debouncedSetFlowAPIEdges]);

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) => {
        const sourceNode = nodes.find((nd) => nd.id === params.source);
        const targetNode = nodes.find((nd) => nd.id === params.target);
        const parentNodeId = nodes.find(
          (nd) => nd.id === params.source
        ).parentNode;
        if (sourceNode.type === "startNode") {
          setNodes((nds) => {
            return [
              ...nds.map((nd) =>
                nd.id === sourceNode.id
                  ? {
                      ...nd,
                      next: params.target,
                    }
                  : nd.id === targetNode.id
                  ? { ...nd, prev: params.source }
                  : nd
              ),
            ];
          });
        } else if (
          sourceNode.type === "userMsgNode" &&
          targetNode.type !== "stopNode"
        ) {
          setNodes((nds) => {
            return [
              ...nds.map((nd) =>
                nd.id === parentNodeId
                  ? {
                      ...nd,
                      user_inputs: nd.user_inputs.map((usr_inp) =>
                        usr_inp.id === params.source
                          ? {
                              ...usr_inp,
                              next: params.target,
                            }
                          : usr_inp
                      ),
                    }
                  : nd.id === targetNode.id
                  ? { ...nd, prev: params.source }
                  : nd
              ),
            ];
          });
        } else if (
          sourceNode.type === "userMsgNode" &&
          targetNode.type === "stopNode"
        ) {
          setNodes((nds) => {
            return [
              ...nds.map((nd) =>
                nd.id === parentNodeId
                  ? {
                      ...nd,
                      user_inputs: nd.user_inputs.map((usr_inp) =>
                        usr_inp.id === params.source
                          ? {
                              ...usr_inp,
                              next: "destination",
                            }
                          : usr_inp
                      ),
                    }
                  : nd.id === targetNode.id
                  ? { ...nd, prev: params.source }
                  : nd
              ),
            ];
          });
        }

        return addEdge(params, eds);
      }),
    [setEdges, nodes, setNodes]
  );

  const onEdgesDelete = useCallback(
    (params) => {
      params.forEach((param) => {
        const sourceNode = nodes.find((nd) => nd.id === param.source);
        const targetNode = nodes.find((nd) => nd.id === param.target);

        if (sourceNode.type === "userMsgNode") {
          setNodes((nds) => {
            return [
              ...nds.map((nd) =>
                nd.id === sourceNode.parentNode
                  ? {
                      ...nd,
                      user_inputs: nd.user_inputs.map((usr_inp) =>
                        usr_inp.id === sourceNode.id
                          ? {
                              ...usr_inp,
                              next: "",
                            }
                          : usr_inp
                      ),
                    }
                  : nd.id === targetNode.id
                  ? { ...nd, prev: "" }
                  : nd
              ),
            ];
          });
        } else if (sourceNode.type === "startNode") {
          setNodes((nds) => {
            return [
              ...nds.map((nd) =>
                nd.id === sourceNode.id
                  ? {
                      ...nd,
                      next: "",
                    }
                  : nd.id === targetNode.id
                  ? { ...nd, prev: "" }
                  : nd
              ),
            ];
          });
        }
      });
    },
    [nodes, setNodes]
  );

  const onNodesDelete = useCallback(
    (deletedNodes) => {
      deletedNodes.forEach((deleted_nd) => {
        if (deleted_nd.type === "userMsgNode") {
          setNodes((nds) => {
            return [
              ...nds.map((nd) =>
                nd.id === deleted_nd.parentNode
                  ? {
                      ...nd,
                      user_inputs: nd.user_inputs.filter(
                        (usr_inp) => usr_inp.id !== deleted_nd.id
                      ),
                    }
                  : nd
              ),
            ];
          });
        }
      });
    },
    [setNodes]
  );

  const addBotMsg = useCallback(() => {
    const id = uuid();
    setNodes((nds) => [
      ...nds,
      {
        id: id,
        type: "botMsgNode",
        position: { x: nds.length * 10, y: nds.length * 10 },
        data: { setNodes: setNodes },
        prev: "",
        message: { en: "", hi: "" },
        imp_msg: false,
        user_inputs: [],
        interruptions: ["all"],
        specific_interruptions: [],
      },
    ]);
  }, [setNodes]);

  const addEndBotMsg = useCallback(() => {
    const id = uuid();
    setNodes((nds) => [
      ...nds,
      {
        id: id,
        type: "botMsgNode",
        position: { x: nds.length * 10, y: nds.length * 10 },
        data: { setNodes: setNodes, isEnd: true },
        prev: "",
        message: { en: "", hi: "" },
        user_inputs: [],
        interruptions: ["STOP"],
        specific_interruptions: [],
      },
    ]);
  }, [setNodes]);

  const addIntermediateStart = useCallback(() => {
    const id = uuid();
    const count = nodes.filter((nd) => nd.type === "startNode").length;
    setNodes((nds) => [
      ...nds,
      {
        id: id,
        type: "startNode",
        next: "",
        position: { x: 50, y: 50 },
        data: { name: `Intermediate_Start_${count}` },
      },
    ]);
  }, [nodes, setNodes]);

  const addIntermediateStop = useCallback(() => {
    const id = uuid();
    const count = nodes.filter((nd) => nd.type === "stopNode").length;
    setNodes((nds) => [
      ...nds,
      {
        id: id,
        type: "stopNode",
        prev: "",
        position: { x: 50, y: 50 },
        data: { name: `Intermediate_Stop_${count + 1}` },
      },
    ]);
  }, [nodes, setNodes]);

  // interruptions

  const [interruptions, setInterruptions] = useState([]);

  useEffect(() => {
    if (!dataFetched) return;

    debouncedSetFlowAPIInterruptions({ interruptions: interruptions });

    // Cleanup function
    return () => {
      debouncedSetFlowAPIInterruptions.cancel();
    };
  }, [interruptions, dataFetched, debouncedSetFlowAPIInterruptions]);

  const addInterruption = () => {
    const id = uuid();
    setInterruptions((prevState) => [
      ...prevState,
      {
        id: id,
        name: "",
        next: "",
        input: "",
        message: { en: "", hi: "" },
        action: "",
      },
    ]);
  };

  const updateInterruption = (id, updateVariable, e) => {
    if (updateVariable === "action") {
      let next = "";
      if (e.target.value === "resume") next = "resume";
      if (e.target.value === "repeat_in_hi" || e.target.value === "repeat")
        next = "self";
      setInterruptions((prevState) =>
        prevState.map((interruption) =>
          interruption.id === id
            ? { ...interruption, [updateVariable]: e.target.value, next: next }
            : interruption
        )
      );
    } else if (updateVariable === "message") {
      setInterruptions((prevState) =>
        prevState.map((interruption) =>
          interruption.id === id
            ? {
                ...interruption,
                [updateVariable]: { en: e.target.value, hi: "" },
              }
            : interruption
        )
      );
    } else {
      setInterruptions((prevState) =>
        prevState.map((interruption) =>
          interruption.id === id
            ? { ...interruption, [updateVariable]: e.target.value }
            : interruption
        )
      );
    }
  };

  const deleteInterruption = (id) => {
    setInterruptions((prevState) =>
      prevState.filter((interruption) => interruption.id !== id)
    );
  };

  //final data (backend json)

  const finalData = useMemo(() => {
    if (!dataFetched) return false;
    return getfinalDataObj(nodes, interruptions);
  }, [nodes, interruptions, dataFetched]);

  //   useEffect(() => {
  //     if (finalData) {
  //       debouncedSetBackendJson({ backend_json: finalData });
  //     }
  //   }, [finalData, debouncedSetBackendJson]);

  //right sidebar

  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);

  // make call popup

  const [makeCallPopupOpen, setMakeCallPopupOpen] = useState(false);

  const toggleMakeCallPopup = () => {
    setMakeCallPopupOpen((prev) => !prev);
  };

  return (
    <>
      <div className="w-screen h-screen">
        <div className="ml-4 mt-4 flex gap-2">
          <PrimaryButton text="Add Bot Message" onClick={addBotMsg} />
          <PrimaryButton text="Add End Bot Message" onClick={addEndBotMsg} />
          <PrimaryButton
            text="Add Intermediate Start"
            onClick={addIntermediateStart}
          />
          <PrimaryButton
            text="Add Intermediate Stop"
            onClick={addIntermediateStop}
          />
          <button
            onClick={toggleMakeCallPopup}
            className="relative ml-auto mr-9 bg-sky-600 rounded-md px-6 text-white">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-6 h-6">
              <path
                fillRule="evenodd"
                d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          {makeCallPopupOpen && (
            <MakeCallPopup
              toggleMakeCallPopup={toggleMakeCallPopup}
              finalData={finalData}
            />
          )}
          {/*<PrimaryButton text="open sidebar translate-x-full" onClick={() => { setRightSidebarOpen(prev => !prev) }} />*/}
        </div>

        <div className="w-[95%] h-[90%] flex justify-center">
          <ReactFlowProvider>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              nodeTypes={nodeTypes}
              panOnScroll={true}
              onEdgesDelete={onEdgesDelete}
              onNodesDelete={onNodesDelete}>
              <Controls />
              <MiniMap />
              <Background variant="dots" gap={12} size={1} />
            </ReactFlow>
          </ReactFlowProvider>
        </div>
      </div>
      {/*{
                <div className={`fixed z-10 h-screen right-0 top-0 bg-white w-[30vw] border border-black ${rightSidebarOpen && 'translate-x-full'} transition-all duration-300 pt-20 pl-4`}>Bot message content will come here.</div>
            }*/}

      <div div className="pl-8">
        <div className="flex items-center text-large">
          <PrimaryButton className="ml-2" text="+" onClick={addInterruption} />
          <span className="ml-5">INTERRUPTIONS</span>
        </div>

        <div className="flex flex-wrap gap-4">
          {interruptions?.map((interruption) => {
            return (
              <div
                key={interruption.id}
                className="bg-yellow-200 w-[25rem] p-4 rounded flex flex-col gap-2 mt-4">
                <div className="flex">
                  <span
                    className="ml-auto hover:blue-500 hover:underline cursor-pointer"
                    onClick={() => {
                      deleteInterruption(interruption.id);
                    }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-6 h-6">
                      <path
                        fillRule="evenodd"
                        d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                </div>
                <div className="flex">
                  <label htmlFor="name">Name:</label>
                  <input
                    value={interruption.name}
                    onChange={(e) => {
                      updateInterruption(interruption.id, "name", e);
                    }}
                    className="border-2 p-2 border-black rounded nodrag ml-auto"
                    id="name"
                    name="name"
                  />
                </div>
                <div className="flex">
                  <label htmlFor="user_input">User Input:</label>
                  <input
                    value={interruption.input}
                    onChange={(e) => {
                      updateInterruption(interruption.id, "input", e);
                    }}
                    className="border-2 p-2 border-black rounded nodrag ml-auto"
                    id="user_input"
                    name="user_input"
                  />
                </div>
                <div className="flex">
                  <label htmlFor="message">Message:</label>
                  <input
                    value={interruption?.message?.en}
                    onChange={(e) => {
                      updateInterruption(interruption.id, "message", e);
                    }}
                    className="border-2 p-2 border-black rounded nodrag ml-auto"
                    id="message"
                    name="message"
                  />
                </div>

                <div className="flex items-center">
                  <label htmlFor="action">Action:</label>
                  <select
                    value={interruption.action}
                    onChange={(e) => {
                      updateInterruption(interruption.id, "action", e);
                    }}
                    className="border-2 p-2 border-black rounded nodrag ml-auto p-0 pl-2"
                    id="action"
                    name="action">
                    <option value="">Select...</option>
                    <option value="repeat">Repeat Message</option>
                    <option value="repeat_in_hi">Repeat in Hindi</option>
                    <option value="set_destination">Set Destination</option>
                    <option value="busy">Hang Up</option>
                    <option value="resume">Pause & Continue</option>
                  </select>
                </div>
                {interruption.action === "set_destination" && (
                  <div className="flex items-center">
                    <label htmlFor="next">Next:</label>
                    <select
                      value={interruption.next}
                      onChange={(e) => {
                        updateInterruption(interruption.id, "next", e);
                      }}
                      className="border-2 p-2 border-black rounded nodrag ml-auto p-0 pl-2"
                      id="next"
                      name="next">
                      <option value="">Select...</option>
                      {nodes
                        .filter(
                          (nd) =>
                            nd.type === "startNode" && nd.data.name !== "Start"
                        )
                        .map((nd) => {
                          return nd.next ? (
                            <option key={nd.id} value={nd.next}>
                              {nd.data.name}
                            </option>
                          ) : null;
                        })}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-[30rem]">
        {/*{'finalData: ' + JSON.stringify(finalData)}*/}
        <FinalDataTextArea
          finalData={finalData}
          setInterruptions={setInterruptions}
          nodes={nodes}
          setNodes={setNodes}
          addEdge={addEdge}
          edges={edges}
          setEdges={setEdges}
        />
      </div>
    </>
  );
};

export default FlowChart;
